import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  QrCodeIcon,
  BanknotesIcon,
  CreditCardIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useGetBankDetailsQuery,
  useUpdateBankDetailsMutation,
  useValidatePaymentMutation,
  useGetPaymentHistoryQuery,
  useGetPendingPaymentsQuery,
  useVerifyPaymentMutation,
  useRejectPaymentMutation,
} from '../../store/api/superAdminApi';
import { formatCurrency, formatDate } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import NotificationDebugger from '../../components/common/NotificationDebugger';
import SubscriptionStatusDebugger from '../../components/common/SubscriptionStatusDebugger';
import toast from 'react-hot-toast';
import socketService from '../../services/socketService';
import { useSelector } from 'react-redux';

const PaymentSystem = () => {
  const [showBankEditModal, setShowBankEditModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [bankFormData, setBankFormData] = useState({});
  const [selectedQRFile, setSelectedQRFile] = useState(null);
  const [previewQR, setPreviewQR] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [validateFormData, setValidateFormData] = useState({
    reference: '',
    transactionId: '',
    amount: '',
    companyId: '',
    subscriptionId: '',
  });

  const { data: bankData, isLoading: bankLoading, refetch: refetchBankDetails } = useGetBankDetailsQuery();
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentHistoryQuery({
    page: 1,
    limit: 10,
  });
  const { data: pendingPaymentsData, isLoading: pendingLoading, refetch: refetchPending } = useGetPendingPaymentsQuery();

  const [updateBankDetails, { isLoading: isUpdatingBank }] = useUpdateBankDetailsMutation();
  const [validatePayment, { isLoading: isValidating }] = useValidatePaymentMutation();
  const [verifyPayment, { isLoading: isVerifying }] = useVerifyPaymentMutation();
  const [rejectPayment, { isLoading: isRejecting }] = useRejectPaymentMutation();

  const bankDetails = bankData?.data || {};
  const payments = paymentsData?.data?.payments || [];
  const pendingPayments = pendingPaymentsData?.data || [];

  // Get current user to check role
  const user = useSelector((state) => state.auth.user);

  // Set up real-time socket listener for new payment notifications
  useEffect(() => {
    if (user?.role === 'super_admin') {
      const socket = socketService.getSocket();
      
      if (socket) {
        // Custom handler for this component
        const handleNewPaymentSubmission = (data) => {
          console.log('💳 PaymentSystem: New payment submission received:', data);
          
          // Automatically refetch pending payments to show the new payment
          refetchPending();
          
          // Show a visual indicator that new payment arrived
          toast.success(`💳 New payment from ${data.companyName}`, {
            duration: 6000,
            icon: '🆕',
            style: {
              background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
              color: '#fff',
              borderLeft: '4px solid #34D399',
              fontWeight: '500'
            },
          });

          // Optional: Add a brief highlight effect to the pending payments section
          const pendingSection = document.querySelector('[data-testid="pending-payments-section"]');
          if (pendingSection) {
            pendingSection.classList.add('ring-2', 'ring-green-400', 'ring-opacity-75');
            setTimeout(() => {
              pendingSection.classList.remove('ring-2', 'ring-green-400', 'ring-opacity-75');
            }, 3000);
          }
        };

        // Listen for new payment submissions
        socket.on('payment:new-submission', handleNewPaymentSubmission);

        // Cleanup listener when component unmounts
        return () => {
          socket.off('payment:new-submission', handleNewPaymentSubmission);
        };
      }
    }
  }, [user?.role, refetchPending]);

  const handleBankEdit = () => {
    setBankFormData(bankDetails);
    setPreviewQR(bankDetails.qrCodeImage || null);
    setSelectedQRFile(null); // Reset selected file
    setShowBankEditModal(true);
  };

  const handleQRFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        // Check file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast.error('File size should be less than 5MB');
          return;
        }
        
        setSelectedQRFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target.result;
          setPreviewQR(result);
          console.log('Preview set:', result ? 'Success' : 'Failed'); // Debug log
        };
        reader.onerror = () => {
          toast.error('Failed to read file');
        };
        reader.readAsDataURL(file);
      } else {
        toast.error('Please select a valid image file (PNG, JPG, JPEG, GIF)');
      }
    }
  };

  const handleRemoveQR = () => {
    setSelectedQRFile(null);
    setPreviewQR(null);
    setBankFormData({ ...bankFormData, qrCodeImage: null });
    
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleBankUpdate = async (e) => {
    e.preventDefault();
    try {
      let updateData = { ...bankFormData };
      
      // If a new QR file is selected, convert to base64
      if (selectedQRFile) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          updateData.qrCodeImage = e.target.result;
          
          console.log('Updating bank details with QR code...'); // Debug log
          const result = await updateBankDetails(updateData).unwrap();
          await refetchBankDetails(); // Refetch to get updated data
          toast.success('Bank details and QR code updated successfully');
          setShowBankEditModal(false);
          setSelectedQRFile(null);
          setPreviewQR(null);
        };
        reader.onerror = () => {
          toast.error('Failed to process QR code image');
        };
        reader.readAsDataURL(selectedQRFile);
      } else {
        console.log('Updating bank details without QR code...'); // Debug log
        const result = await updateBankDetails(updateData).unwrap();
        await refetchBankDetails(); // Refetch to get updated data
        toast.success('Bank details updated successfully');
        setShowBankEditModal(false);
      }
    } catch (error) {
      console.error('Bank update error:', error); // Debug log
      toast.error('Failed to update bank details');
    }
  };

  const handleValidatePayment = async (e) => {
    e.preventDefault();
    try {
      await validatePayment(validateFormData).unwrap();
      toast.success('Payment validated successfully');
      setShowValidateModal(false);
      setValidateFormData({
        reference: '',
        transactionId: '',
        amount: '',
        companyId: '',
        subscriptionId: '',
      });
    } catch (error) {
      toast.error('Failed to validate payment');
    }
  };

  const handleVerifyPayment = async (paymentId, notes = '') => {
    try {
      const result = await verifyPayment({ paymentId, notes }).unwrap();
      
      // Show detailed success message based on what happened
      if (result.data.upgradeApplied) {
        toast.success('Payment verified and subscription upgraded successfully! 🎉', {
          duration: 6000,
          icon: '✅',
        });
      } else {
        toast.success('Payment verified and subscription activated successfully! 🎉', {
          duration: 6000,
          icon: '✅',
        });
      }
      
      setShowVerifyModal(false);
      setShowPaymentDetailsModal(false);
      setVerificationNotes('');
      refetchPending();
    } catch (error) {
      console.error('Verify payment error:', error);
      toast.error(error.data?.message || 'Failed to verify payment');
    }
  };

  const handleRejectPayment = async (paymentId, reason = '') => {
    try {
      const result = await rejectPayment({ paymentId, reason }).unwrap();
      
      // Show success message with detailed information
      toast.success('Payment rejected successfully! Subscription reverted to trial mode.', {
        duration: 6000,
        icon: '❌',
        style: {
          background: '#FEF3C7',
          color: '#92400E',
          border: '1px solid #F59E0B'
        }
      });
      
      setShowRejectModal(false);
      setShowPaymentDetailsModal(false);
      setRejectionReason('');
      refetchPending();
      
      // Also show a follow-up notification about subscription status
      setTimeout(() => {
        toast('💡 The company subscription has been reverted to trial mode with limited features.', {
          duration: 5000,
          icon: '🔄',
          style: {
            background: '#EFF6FF',
            color: '#1E40AF',
            border: '1px solid #3B82F6'
          }
        });
      }, 1000);
      
    } catch (error) {
      console.error('Reject payment error:', error);
      toast.error(error.data?.message || 'Failed to reject payment');
    }
  };

  const openVerifyModal = (payment) => {
    setSelectedPayment(payment);
    setVerificationNotes('');
    setShowVerifyModal(true);
  };

  const openRejectModal = (payment) => {
    setSelectedPayment(payment);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const viewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentDetailsModal(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateDaysSince = (date) => {
    const now = new Date();
    const createdAt = new Date(date);
    const diffTime = Math.abs(now - createdAt);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (bankLoading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Payment System</h1>
          <p className="text-gray-600 mt-2">Manage bank details, generate QR codes, and validate payments</p>
        </div>

        {/* Notification Debugger - Only shows for super admins or in development */}
        <NotificationDebugger />

        {/* Subscription Status Monitor - For debugging payment workflow */}
        <SubscriptionStatusDebugger />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Bank Details Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <BanknotesIcon className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Bank Details</h2>
              </div>
              <button
                onClick={handleBankEdit}
                className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Bank Name</label>
                  <p className="text-gray-900 font-medium">{bankDetails.bankName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Name</label>
                  <p className="text-gray-900 font-medium">{bankDetails.accountName}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Account Number</label>
                  <div className="flex items-center">
                    <p className="text-gray-900 font-mono">{bankDetails.accountNumber}</p>
                    <button
                      onClick={() => copyToClipboard(bankDetails.accountNumber)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">IFSC Code</label>
                  <div className="flex items-center">
                    <p className="text-gray-900 font-mono">{bankDetails.ifscCode}</p>
                    <button
                      onClick={() => copyToClipboard(bankDetails.ifscCode)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">UPI ID</label>
                <div className="flex items-center">
                  <p className="text-gray-900 font-mono">{bankDetails.upiId}</p>
                  <button
                    onClick={() => copyToClipboard(bankDetails.upiId)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <p className="text-gray-900">
                  {bankDetails.address?.street}, {bankDetails.address?.city}<br />
                  {bankDetails.address?.state}, {bankDetails.address?.country} - {bankDetails.address?.zipCode}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-gray-600 text-sm">{bankDetails.notes}</p>
              </div>

              {bankDetails.qrCodeImage && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-gray-500 mb-3 block">UPI QR Code</label>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        src={bankDetails.qrCodeImage}
                        alt="UPI QR Code"
                        className="w-32 h-32 border rounded-lg object-contain cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setShowQRModal(true)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-2">
                        Scan this QR code to make UPI payments to <strong>{bankDetails.upiId}</strong>
                      </p>
                      <button
                        onClick={() => setShowQRModal(true)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Full Size →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* UPI QR Code Management */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center mb-6">
              <QrCodeIcon className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">UPI QR Code</h2>
            </div>

            <div className="space-y-4">
              {bankDetails.qrCodeImage ? (
                <div className="text-center">
                  <div className="mb-4">
                    <img
                      src={bankDetails.qrCodeImage}
                      alt="UPI QR Code"
                      className="w-64 h-64 mx-auto border rounded-lg object-contain"
                    />
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>UPI ID:</strong> {bankDetails.upiId}
                    </p>
                    <p className="text-sm text-gray-600">
                      Customers can scan this QR code to make payments directly to your UPI account.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowQRModal(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                  >
                    View Full Size
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <QrCodeIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No QR Code Uploaded</h3>
                  <p className="text-gray-600 mb-4">
                    Upload a UPI QR code image to allow customers to make payments easily.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    The QR code should correspond to your UPI ID: {bankDetails.upiId}
                  </p>
                  <button
                    onClick={handleBankEdit}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center mx-auto"
                  >
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    Upload QR Code
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Payment Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 space-y-6"
        >
          {/* Pending Payments for Verification */}
          <div 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all duration-300" 
            data-testid="pending-payments-section"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Pending Payment Verification</h2>
                  <p className="text-sm text-gray-600">Review and verify customer payments to activate their accounts</p>
                </div>
              </div>
              <div className="bg-yellow-100 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-yellow-800">
                  {pendingPayments.length} pending
                </span>
              </div>
            </div>

            {pendingLoading ? (
              <LoadingSpinner />
            ) : pendingPayments.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-600">No pending payments to verify</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference No.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attachments</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingPayments.map((payment) => (
                      <tr key={payment._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.companyId?.name || 'Unknown Company'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {payment.customerInfo?.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {payment.planId?.name || 'Unknown Plan'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">
                            {formatCurrency(payment.amount, payment.currency)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {payment.billingCycle}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-mono bg-gray-100 px-2 py-1 rounded text-gray-900">
                            {payment.referenceNumber}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <span className="capitalize">{payment.paymentMethod?.replace('_', ' ')}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center">
                            {payment.attachments && payment.attachments.length > 0 ? (
                              <div className="flex items-center">
                                <DocumentTextIcon className="h-4 w-4 text-green-600 mr-1" />
                                <span className="text-green-600 font-medium">{payment.attachments.length}</span>
                                <span className="text-gray-500 ml-1">file{payment.attachments.length > 1 ? 's' : ''}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">No attachments</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(payment.createdAt)}
                          <div className="text-xs">
                            {calculateDaysSince(payment.createdAt)} days ago
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => viewPaymentDetails(payment)}
                            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <EyeIcon className="w-4 h-4 mr-1" />
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Payment History</h2>
              </div>
              <button
                onClick={() => setShowValidateModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Manual Entry
              </button>
            </div>

            {paymentsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment._id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {payment.companyId?.name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {payment.planId?.name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatCurrency(payment.price || payment.amount, payment.currency || 'INR')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.paymentStatus === 'paid' || payment.status === 'verified'
                              ? 'bg-green-100 text-green-800'
                              : payment.paymentStatus === 'pending' || payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {payment.paymentStatus || payment.status || 'pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(payment.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Bank Edit Modal */}
      {showBankEditModal && (
        <Modal
          isOpen={showBankEditModal}
          onClose={() => {
            setShowBankEditModal(false);
            setSelectedQRFile(null);
            setPreviewQR(null);
          }}
          title="Edit Bank Details"
          size="lg"
        >
          <form onSubmit={handleBankUpdate} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  value={bankFormData.bankName || ''}
                  onChange={(e) => setBankFormData({ ...bankFormData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                <input
                  type="text"
                  value={bankFormData.accountName || ''}
                  onChange={(e) => setBankFormData({ ...bankFormData, accountName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                <input
                  type="text"
                  value={bankFormData.accountNumber || ''}
                  onChange={(e) => setBankFormData({ ...bankFormData, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                <input
                  type="text"
                  value={bankFormData.ifscCode || ''}
                  onChange={(e) => setBankFormData({ ...bankFormData, ifscCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">UPI ID</label>
              <input
                type="text"
                value={bankFormData.upiId || ''}
                onChange={(e) => setBankFormData({ ...bankFormData, upiId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={bankFormData.notes || ''}
                onChange={(e) => setBankFormData({ ...bankFormData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* QR Code Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">UPI QR Code</label>
              <div className="space-y-4">
                {previewQR ? (
                  <div className="bg-gray-50 border rounded-lg p-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src={previewQR}
                          alt="QR Code Preview"
                          className="w-32 h-32 border-2 border-gray-200 rounded-lg object-contain bg-white"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {selectedQRFile ? 'New QR Code Selected' : 'Current QR Code'}
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          {selectedQRFile 
                            ? `File: ${selectedQRFile.name} (${Math.round(selectedQRFile.size / 1024)} KB)`
                            : 'This QR code will be displayed to customers for UPI payments.'
                          }
                        </p>
                        <button
                          type="button"
                          onClick={handleRemoveQR}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center font-medium"
                        >
                          <TrashIcon className="w-4 h-4 mr-1" />
                          {selectedQRFile ? 'Cancel Selection' : 'Remove QR Code'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                    <PhotoIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm font-medium text-gray-700 mb-1">Upload a QR code image</p>
                    <p className="text-sm text-gray-500 mb-2">For UPI payments from customers</p>
                    <p className="text-xs text-gray-400">PNG, JPG, JPEG, GIF up to 5MB</p>
                  </div>
                )}
                
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQRFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Select a clear QR code image that corresponds to your UPI ID: <strong>{bankFormData.upiId}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowBankEditModal(false);
                  setSelectedQRFile(null);
                  setPreviewQR(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdatingBank}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {isUpdatingBank ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* QR Code Display Modal */}
      {showQRModal && bankDetails.qrCodeImage && (
        <Modal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          title="UPI QR Code"
          size="lg"
        >
          <div className="p-6 text-center">
            <div className="mb-6">
              <img
                src={bankDetails.qrCodeImage}
                alt="UPI QR Code"
                className="mx-auto border rounded-lg max-w-full h-auto"
              />
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <p><strong>UPI ID:</strong> {bankDetails.upiId}</p>
                <p><strong>Account Name:</strong> {bankDetails.accountName}</p>
                <p><strong>Bank:</strong> {bankDetails.bankName}</p>
                <p><strong>Account Number:</strong> {bankDetails.accountNumber}</p>
                <p><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
              </div>
            </div>
            <div className="text-left">
              <h4 className="font-medium text-gray-900 mb-2">Instructions for Customers:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Scan the QR code with any UPI-enabled app</li>
                <li>• Enter the payment amount as instructed</li>
                <li>• Add your company name in the remarks/reference</li>
                <li>• Complete the payment and save the transaction ID</li>
                <li>• Share the transaction details for verification</li>
              </ul>
            </div>
            <div className="mt-6 pt-4 border-t">
              <button
                onClick={() => copyToClipboard(bankDetails.upiId)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 mr-3"
              >
                Copy UPI ID
              </button>
              <button
                onClick={() => setShowQRModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Payment Validation Modal */}
      {showValidateModal && (
        <Modal
          isOpen={showValidateModal}
          onClose={() => setShowValidateModal(false)}
          title="Validate Payment"
        >
          <form onSubmit={handleValidatePayment} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
              <input
                type="text"
                required
                value={validateFormData.reference}
                onChange={(e) => setValidateFormData({ ...validateFormData, reference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter reference number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID</label>
              <input
                type="text"
                required
                value={validateFormData.transactionId}
                onChange={(e) => setValidateFormData({ ...validateFormData, transactionId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter transaction ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <input
                type="number"
                step="0.01"
                required
                value={validateFormData.amount}
                onChange={(e) => setValidateFormData({ ...validateFormData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subscription ID (Optional)</label>
              <input
                type="text"
                value={validateFormData.subscriptionId}
                onChange={(e) => setValidateFormData({ ...validateFormData, subscriptionId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter subscription ID if applicable"
              />
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => setShowValidateModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isValidating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isValidating ? 'Validating...' : 'Validate Payment'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Payment Details Modal */}
      {showPaymentDetailsModal && selectedPayment && (
        <Modal
          isOpen={showPaymentDetailsModal}
          onClose={() => setShowPaymentDetailsModal(false)}
          title="Payment Verification"
          size="lg"
        >
          <div className="p-6 space-y-6">
            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Company</p>
                  <p className="font-medium text-gray-900">{selectedPayment.companyId?.name || 'Unknown Company'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact Person</p>
                  <p className="font-medium text-gray-900">{selectedPayment.customerInfo?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{selectedPayment.customerInfo?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900">{selectedPayment.customerInfo?.phone || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Plan</p>
                  <p className="font-medium text-gray-900">{selectedPayment.planId?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="font-medium text-gray-900 text-lg">
                    {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Billing Cycle</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedPayment.billingCycle}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="font-medium text-gray-900 capitalize">{selectedPayment.paymentMethod?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reference Number</p>
                  <p className="font-mono font-medium text-gray-900 bg-white px-2 py-1 rounded">
                    {selectedPayment.referenceNumber}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Submitted</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedPayment.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Attachments */}
            {selectedPayment.attachments && selectedPayment.attachments.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Payment Proof Attachments ({selectedPayment.attachments.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedPayment.attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3 border">
                      <div className="flex items-center flex-1">
                        <div className="flex-shrink-0 mr-3">
                          {attachment.mimeType?.startsWith('image/') ? (
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                              <PhotoIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={attachment.originalName}>
                            {attachment.originalName}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>{formatFileSize(attachment.size)}</span>
                            <span>•</span>
                            <span className="capitalize">{attachment.mimeType?.split('/')[1] || 'Unknown'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        {attachment.mimeType?.startsWith('image/') && (
                          <button
                            onClick={() => {
                              // Create a preview modal for images using base64 data
                              const imgWindow = window.open('', '_blank');
                              imgWindow.document.write(`
                                <html>
                                  <head><title>Payment Proof - ${attachment.originalName}</title></head>
                                  <body style="margin:0;padding:20px;text-align:center;background:#f5f5f5;">
                                    <h3>${attachment.originalName}</h3>
                                    <img src="${attachment.data || `/api/uploads/${attachment.filename}`}" style="max-width:100%;max-height:90vh;border:1px solid #ddd;border-radius:8px;background:white;padding:10px;" />
                                  </body>
                                </html>
                              `);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            title="Preview Image"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (attachment.data) {
                              // Download base64 data
                              const link = document.createElement('a');
                              link.href = attachment.data;
                              link.download = attachment.originalName || attachment.filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            } else {
                              // Fallback to file path
                              window.open(`/api/uploads/${attachment.filename}`, '_blank');
                            }
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                          title="Download/View"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedPayment.notes && (
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700">{selectedPayment.notes}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setShowPaymentDetailsModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => openRejectModal(selectedPayment)}
                disabled={isRejecting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
              >
                <XMarkIcon className="w-4 h-4 mr-2" />
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                onClick={() => openVerifyModal(selectedPayment)}
                disabled={isVerifying}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                {isVerifying ? 'Verifying...' : 'Verify & Activate'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Payment Verification Modal */}
      {showVerifyModal && selectedPayment && (
        <Modal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          title="Verify Payment"
        >
          <div className="p-6">
            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Payment Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-green-700 font-medium">Company:</span>
                    <span className="ml-2 text-green-900">{selectedPayment.companyId?.name}</span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Plan:</span>
                    <span className="ml-2 text-green-900">{selectedPayment.planId?.name}</span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Amount:</span>
                    <span className="ml-2 text-green-900 font-semibold">
                      {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                    </span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Reference:</span>
                    <span className="ml-2 text-green-900 font-mono">{selectedPayment.referenceNumber}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Notes <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  placeholder="Add any notes about this payment verification (transaction ID verified, amount confirmed, etc.)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These notes will be saved and can help with future reference or auditing.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowVerifyModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleVerifyPayment(selectedPayment._id, verificationNotes)}
                disabled={isVerifying}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
              >
                {isVerifying ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Verify & Activate Account
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Payment Rejection Modal */}
      {showRejectModal && selectedPayment && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          title="Reject Payment"
        >
          <div className="p-6">
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                  <h3 className="text-lg font-semibold text-red-900">Payment Rejection</h3>
                </div>
                <p className="text-red-800 text-sm mb-3">
                  Are you sure you want to reject this payment? This action will:
                </p>
                <ul className="text-red-800 text-sm space-y-1 ml-4">
                  <li>• Revert the subscription back to trial mode</li>
                  <li>• Notify the customer about the rejection</li>
                  <li>• Require them to make a new payment attempt</li>
                </ul>
                
                <div className="mt-3 pt-3 border-t border-red-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-red-700 font-medium">Company:</span>
                      <span className="ml-2 text-red-900">{selectedPayment.companyId?.name}</span>
                    </div>
                    <div>
                      <span className="text-red-700 font-medium">Amount:</span>
                      <span className="ml-2 text-red-900 font-semibold">
                        {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="Please provide a clear reason for rejecting this payment (e.g., incorrect amount, invalid transaction ID, payment not received, etc.)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be sent to the customer to help them understand the rejection.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRejectPayment(selectedPayment._id, rejectionReason)}
                disabled={isRejecting || !rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
              >
                {isRejecting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Reject Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaymentSystem;